import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ListChecks, CheckCircle2, XCircle, Sigma, Trophy, Printer, Hourglass, BookOpen, BarChart3, GitBranch, FlaskConical, Brain, Target, CalendarDays, FileText } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { printReportCard, printExamResult, printBranchResult } from "@/lib/reportCard";

const YKS_DATE = new Date("2027-06-12T09:00:00");
const AYT_DATE = new Date("2027-06-13T09:00:00");
const daysLeft = (d) => Math.max(0, Math.ceil((d - new Date()) / 86400000));

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div className="card-ta p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</span>
      <Icon className={color} size={18} />
    </div>
    <div className="font-display text-2xl font-extrabold mt-2 text-slate-900">{value}</div>
  </div>
);

export default function Overview({ studentId, studentName, track }) {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [sections, setSections] = useState([]);
  const [units, setUnits] = useState([]);
  const [exams, setExams] = useState([]);
  const [daily, setDaily] = useState([]);
  const [branchExams, setBranchExams] = useState([]);
  const [pppd, setPppd] = useState([]);
  const [examEvents, setExamEvents] = useState([]);
  const [suSubject, setSuSubject] = useState("");
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [printDoc, setPrintDoc] = useState("");

  const karne = async () => {
    setPrinting(true);
    try {
      const ok = await printReportCard(studentId, studentName || "Öğrenci", track);
      if (!ok) toast.error("Karne penceresi açılamadı. Tarayıcı pop-up iznini kontrol edin.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPrinting(false);
    }
  };

  const printResult = async (kind) => {
    setPrintDoc(kind);
    try {
      const ok = kind === "BRANS"
        ? await printBranchResult(studentId, studentName || "Öğrenci", track)
        : await printExamResult(studentId, studentName || "Öğrenci", track, kind);
      if (!ok) toast.error("Belge penceresi açılamadı. Pop-up iznini kontrol edin.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPrintDoc("");
    }
  };

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    Promise.all([
      api.get(`/analytics/overview/${studentId}`),
      api.get(`/resource-plans/${studentId}`),
      api.get(`/resource-sections/${studentId}`),
      api.get(`/unit-progress/${studentId}`),
      api.get(`/general-exams/${studentId}`),
      api.get(`/daily-questions/${studentId}`),
      api.get(`/branch-exams/${studentId}`),
      api.get(`/pppd/${studentId}`),
      api.get("/exam-events"),
    ])
      .then(([o, p, s, u, e, d, b, pp, ev]) => {
        setData(o.data);
        setPlans(p.data);
        setSections(s.data);
        setUnits(u.data);
        setExams(e.data);
        setDaily(d.data);
        setBranchExams(b.data);
        setPppd(pp.data);
        setExamEvents(ev.data);
      })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading)
    return (
      <div className="py-10 text-center text-slate-500">
        <Loader2 className="animate-spin inline" size={22} />
      </div>
    );
  if (!data) return null;

  const latestExams = [...exams].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 8);
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = examEvents
    .filter((e) => (e.date || "") >= today && (!e.track || !track || e.track === track))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    .slice(0, 8);

  const suAgg = {};
  daily.forEach((q) => {
    const key = `${q.subject || "Genel"}__${q.unit_name || "Genel"}`;
    const a = (suAgg[key] = suAgg[key] || { subject: q.subject || "Genel", unit: q.unit_name || "Genel", total: 0, correct: 0, wrong: 0, blank: 0 });
    a.total += Number(q.total || 0);
    a.correct += Number(q.correct || 0);
    a.wrong += Number(q.wrong || 0);
    a.blank += Number(q.blank || 0);
  });
  const suRows = Object.values(suAgg).sort((a, b) => b.total - a.total);
  const suSubjects = [...new Set(suRows.map((r) => r.subject))];
  const suSel = suSubjects.includes(suSubject) ? suSubject : suSubjects[0];
  const suFiltered = suRows.filter((r) => r.subject === suSel);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold">Genel Bakış {studentName ? `· ${studentName}` : ""}</h3>
          <p className="text-sm text-slate-500 mt-0.5">{track || "Alan belirtilmemiş"} · Sınav geri sayımı, kaynak ve ünite durumu.</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="result-docs">
          <Button onClick={() => printResult("TYT")} disabled={!!printDoc} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="doc-tyt-btn">
            {printDoc === "TYT" ? <Loader2 className="animate-spin" size={15} /> : <FileText size={15} />}
            <span className="ml-1">TYT Sonuç Belgesi</span>
          </Button>
          <Button onClick={() => printResult("AYT")} disabled={!!printDoc} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="doc-ayt-btn">
            {printDoc === "AYT" ? <Loader2 className="animate-spin" size={15} /> : <FileText size={15} />}
            <span className="ml-1">AYT Sonuç Belgesi</span>
          </Button>
          <Button onClick={() => printResult("BRANS")} disabled={!!printDoc} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="doc-brans-btn">
            {printDoc === "BRANS" ? <Loader2 className="animate-spin" size={15} /> : <FlaskConical size={15} />}
            <span className="ml-1">Branş Sonuç Belgesi</span>
          </Button>
          <Button onClick={karne} disabled={printing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="karne-btn">
            {printing ? <Loader2 className="animate-spin" size={16} /> : <Printer size={16} />}
            <span className="ml-1">Genel Karne</span>
          </Button>
        </div>
      </div>

      {/* YKS / AYT geri sayım */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card-ta p-5 border-l-4 border-l-indigo-500" data-testid="countdown-yks">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">YKS (TYT) 2027</span>
            <Hourglass className="text-indigo-500" size={18} />
          </div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-1">
            {daysLeft(YKS_DATE)} <span className="text-base font-semibold text-slate-500">gün kaldı</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">12 Haziran 2027</div>
        </div>
        <div className="card-ta p-5 border-l-4 border-l-amber-500" data-testid="countdown-ayt">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">AYT 2027</span>
            <Hourglass className="text-amber-500" size={18} />
          </div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-1">
            {daysLeft(AYT_DATE)} <span className="text-base font-semibold text-slate-500">gün kaldı</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">13 Haziran 2027</div>
        </div>
      </div>

      {/* Yaklaşan Sınavlar */}
      <div className="card-ta overflow-hidden" data-testid="overview-upcoming-exams">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <CalendarDays className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">Yaklaşan Sınavlar</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium">Tarih</th>
                <th className="px-4 py-2.5 font-medium">Sınav</th>
                <th className="px-4 py-2.5 font-medium">Tür</th>
                <th className="px-4 py-2.5 font-medium">Alan</th>
                <th className="px-4 py-2.5 font-medium text-right">Kalan Gün</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Yaklaşan sınav yok.</td></tr>
              ) : (
                upcoming.map((e) => {
                  const dl = daysLeft(new Date(`${e.date}T09:00:00`));
                  return (
                    <tr key={e.id} className="border-b border-slate-100" data-testid={`upcoming-exam-${e.id}`}>
                      <td className="px-4 py-2.5 font-semibold text-slate-800">
                        {new Date(`${e.date}T09:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "short" })}
                      </td>
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{e.title}</td>
                      <td className="px-4 py-2.5">
                        {e.exam_type ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${e.exam_type === "TYT" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>{e.exam_type}</span>
                        ) : "-"}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{e.track || "Tümü"}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${dl <= 7 ? "bg-rose-100 text-rose-700" : dl <= 30 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`} data-testid={`upcoming-days-${e.id}`}>
                          {dl === 0 ? "BUGÜN" : `${dl} gün`}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Branş Deneme Takvimi */}
      <div className="card-ta overflow-hidden" data-testid="overview-branch-calendar">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <FlaskConical className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">Branş Deneme Takvimi</h4>
        </div>
        <div className="overflow-auto max-h-96">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium">Tarih</th>
                <th className="px-4 py-2.5 font-medium">Branş</th>
                <th className="px-4 py-2.5 font-medium">Deneme</th>
                <th className="px-4 py-2.5 font-medium text-right">Durum</th>
              </tr>
            </thead>
            <tbody>
              {branchExams.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Branş denemesi planlanmamış.</td></tr>
              ) : (
                [...branchExams]
                  .sort((a, b) => {
                    const af = (a.date || "") >= today;
                    const bf = (b.date || "") >= today;
                    if (af && bf) return (a.date || "").localeCompare(b.date || "");
                    if (!af && !bf) return (b.date || "").localeCompare(a.date || "");
                    return af ? -1 : 1;
                  })
                  .map((e) => {
                  const future = (e.date || "") >= today;
                  const dl = future ? daysLeft(new Date(`${e.date}T09:00:00`)) : 0;
                  return (
                    <tr key={e.id} className="border-b border-slate-100" data-testid={`branch-cal-${e.id}`}>
                      <td className="px-4 py-2.5 font-semibold text-slate-800">
                        {e.date ? new Date(`${e.date}T09:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "short" }) : "-"}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs px-2 py-0.5 rounded-full border bg-violet-100 text-violet-700 border-violet-200 font-semibold">{e.branch}</span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{e.name || "-"}</td>
                      <td className="px-4 py-2.5 text-right">
                        {future ? (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${dl <= 7 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                            {dl === 0 ? "BUGÜN" : `${dl} gün kaldı`}
                          </span>
                        ) : (
                          <span className="font-mono font-semibold text-indigo-700">{Number(e.net || 0).toFixed(2)} net</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={ListChecks} label="Toplam Soru" value={data.total_questions} color="text-blue-600" testid="stat-total" />
        <Stat icon={CheckCircle2} label="Doğru" value={data.correct} color="text-emerald-600" testid="stat-correct" />
        <Stat icon={XCircle} label="Yanlış" value={data.wrong} color="text-rose-600" testid="stat-wrong" />
        <Stat icon={Sigma} label="Toplam Net" value={Number(data.net).toFixed(1)} color="text-indigo-600" testid="stat-net" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-ta p-4">
          <div className="text-sm text-slate-500 mb-3">Günlük Çözülen Soru</div>
          {data.by_day.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm">Henüz soru verisi yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.by_day}>
                <defs>
                  <linearGradient id="qg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                <Area type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} fill="url(#qg)" name="Soru" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="card-ta p-5 space-y-4">
          <div className="text-sm text-slate-500">Deneme Ortalamaları</div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Trophy className="text-blue-600" size={18} />
            </div>
            <div>
              <div className="text-xs text-slate-500">Ortalama TYT Net</div>
              <div className="font-display text-xl font-bold text-blue-600">{data.avg_tyt_net}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Trophy className="text-amber-600" size={18} />
            </div>
            <div>
              <div className="text-xs text-slate-500">Ortalama AYT Net</div>
              <div className="font-display text-xl font-bold text-amber-600">{data.avg_ayt_net}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 pt-2 border-t border-slate-200">{data.targets.length} hedef bölüm tanımlı</div>
        </div>
      </div>

      {/* Ders & Ünite Bazlı Soru Takibi */}
      <div className="card-ta overflow-hidden" data-testid="overview-subject-units">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <ListChecks className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">Ders & Ünite Bazlı Soru Takibi</h4>
        </div>
        {suSubjects.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-slate-100">
            {suSubjects.map((sub) => (
              <button
                key={sub}
                onClick={() => setSuSubject(sub)}
                data-testid={`su-btn-${sub}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  suSel === sub ? "bg-indigo-600 text-white border-indigo-600 shadow-sm" : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400 hover:text-indigo-600"
                }`}
              >
                {sub}
              </button>
            ))}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium">Ders</th>
                <th className="px-4 py-2.5 font-medium">Ünite/Konu</th>
                <th className="px-4 py-2.5 font-medium text-right">Toplam</th>
                <th className="px-4 py-2.5 font-medium text-right">Doğru</th>
                <th className="px-4 py-2.5 font-medium text-right">Yanlış</th>
                <th className="px-4 py-2.5 font-medium text-right">Boş</th>
                <th className="px-4 py-2.5 font-medium text-right">Net</th>
                <th className="px-4 py-2.5 font-medium text-right">Başarı</th>
              </tr>
            </thead>
            <tbody>
              {suRows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Soru verisi yok.</td></tr>
              ) : (
                suFiltered.map((r, i) => {
                  const net = r.correct - r.wrong / 4;
                  const acc = r.total ? Math.round((r.correct / r.total) * 100) : 0;
                  return (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{r.subject}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.unit}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-slate-800">{r.total}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-700 font-semibold">{r.correct}</td>
                      <td className="px-4 py-2.5 text-right text-rose-700 font-semibold">{r.wrong}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{r.blank}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold text-indigo-700">{net.toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${acc >= 70 ? "bg-emerald-100 text-emerald-700" : acc >= 50 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>%{acc}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bitirilen Kaynak Takipleri */}
      <div className="card-ta p-4" data-testid="overview-plans">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">Kaynak Takibi — Bitme Durumu</h4>
        </div>
        {plans.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">Kaynak planı yok.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {plans.map((p) => {
              const ps = sections.filter((s) => s.plan_id === p.id);
              const done = ps.filter((s) => s.completed);
              const pct = ps.length ? Math.round((done.length / ps.length) * 100) : 0;
              const solved = done.reduce((a, s) => a + (Number(s.total) || 0), 0);
              return (
                <div key={p.id} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm text-slate-900">{p.name}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{p.resource_type}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{p.weeks} hafta · {done.length}/{ps.length} bölüm bitti · {solved} soru çözüldü</div>
                  <div className="mt-2 flex items-center gap-2">
                    <Progress value={pct} className="h-2 flex-1" />
                    <span className="text-xs font-mono text-slate-600">%{pct}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sınav Sonuç Tablosu */}
      <div className="card-ta overflow-hidden" data-testid="overview-exams">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <BarChart3 className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">Sınav Sonuç Tablosu</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium">Tarih</th>
                <th className="px-4 py-2.5 font-medium">Sınav</th>
                <th className="px-4 py-2.5 font-medium">Deneme</th>
                <th className="px-4 py-2.5 font-medium text-right">Net</th>
                <th className="px-4 py-2.5 font-medium text-right">Sıralama</th>
              </tr>
            </thead>
            <tbody>
              {latestExams.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Deneme kaydı yok.</td></tr>
              ) : (
                latestExams.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5">{e.date || "-"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${e.exam_type === "TYT" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>{e.exam_type}</span>
                    </td>
                    <td className="px-4 py-2.5">{e.name || "-"}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">{Number(e.total_net || 0).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{e.ranking ? Number(e.ranking).toLocaleString("tr-TR") : "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Branş Deneme Sonuç Tablosu */}
      <div className="card-ta overflow-hidden" data-testid="overview-branch-exams">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <FlaskConical className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">Branş Deneme Sonuç Tablosu</h4>
        </div>
        <div className="overflow-auto max-h-96">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium">Tarih</th>
                <th className="px-4 py-2.5 font-medium">Branş</th>
                <th className="px-4 py-2.5 font-medium">Deneme</th>
                <th className="px-4 py-2.5 font-medium text-right">Toplam</th>
                <th className="px-4 py-2.5 font-medium text-right">Doğru</th>
                <th className="px-4 py-2.5 font-medium text-right">Yanlış</th>
                <th className="px-4 py-2.5 font-medium text-right">Boş</th>
                <th className="px-4 py-2.5 font-medium text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {branchExams.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Branş denemesi kaydı yok.</td></tr>
              ) : (
                [...branchExams].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e) => (
                  <tr key={e.id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5">{e.date || "-"}</td>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">{e.branch}</td>
                    <td className="px-4 py-2.5 text-slate-600">{e.name || "-"}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-800">{e.total}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-700 font-semibold">{e.correct}</td>
                    <td className="px-4 py-2.5 text-right text-rose-700 font-semibold">{e.wrong}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{e.blank ?? 0}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-indigo-700">{Number(e.net || 0).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ünite Bitirme Durumu */}
      <div className="card-ta p-4" data-testid="overview-units">
        <div className="flex items-center gap-2 mb-3">
          <GitBranch className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">Ünite Bitirme Durumu</h4>
        </div>
        {units.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">Ünite ilerlemesi girilmemiş.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {[...units].sort((a, b) => Number(a.completion || 0) - Number(b.completion || 0)).map((u) => {
              const v = Math.min(100, Number(u.completion || 0));
              return (
                <div key={u.id} className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${u.exam === "AYT" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{u.exam || "-"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-700 truncate">{u.subject} · {u.unit_name}</span>
                      <span className="font-mono text-slate-500">%{v}</span>
                    </div>
                    <Progress value={v} className="h-1.5 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PPD Sonuç Tablosu */}
      <div className="card-ta overflow-hidden" data-testid="overview-pppd">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <Brain className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">PPD Sonuç Tablosu</h4>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {["Paragraf", "Problem", "Dilbilgisi", "Geometri"].map((t) => {
            const rows = pppd.filter((e) => e.type === t);
            const ag = (cat) =>
              rows.filter((e) => e.category === cat).reduce(
                (a, e) => ({
                  total: a.total + Number(e.total || 0),
                  correct: a.correct + Number(e.correct || 0),
                  wrong: a.wrong + Number(e.wrong || 0),
                  blank: a.blank + Number(e.blank || 0),
                }),
                { total: 0, correct: 0, wrong: 0, blank: 0 }
              );
            const soru = ag("Soru");
            const deneme = ag("Deneme");
            const head = { Paragraf: "bg-blue-600", Problem: "bg-amber-500", Dilbilgisi: "bg-emerald-600", Geometri: "bg-purple-600" }[t];
            const soft = { Paragraf: "bg-blue-50 border-blue-200", Problem: "bg-amber-50 border-amber-200", Dilbilgisi: "bg-emerald-50 border-emerald-200", Geometri: "bg-purple-50 border-purple-200" }[t];
            return (
              <div key={t} className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`ov-pppd-${t}`}>
                <div className={`${head} text-white px-3 py-1.5 text-sm font-bold flex items-center justify-between`}>
                  {t}
                  <span className="text-[10px] font-semibold opacity-90">{rows.length} kayıt</span>
                </div>
                <div className="grid grid-cols-2 gap-2 p-2.5">
                  {[{ label: "Soru", a: soru }, { label: "Deneme", a: deneme }].map((blk) => (
                    <div key={blk.label} className={`rounded border p-2 ${soft}`}>
                      <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">{blk.label}</div>
                      <div className="text-[11px] text-slate-700 space-y-0.5">
                        <div>Top: <b>{blk.a.total}</b> · D: <b className="text-emerald-700">{blk.a.correct}</b> · Y: <b className="text-rose-700">{blk.a.wrong}</b> · Boş: <b>{blk.a.blank}</b></div>
                        <div>Net: <b className="text-indigo-700">{(blk.a.correct - blk.a.wrong / 4).toFixed(2)}</b></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hedef Net Tablosu */}
      <div className="card-ta overflow-hidden" data-testid="overview-targets">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <Target className="text-indigo-600" size={18} />
          <h4 className="font-display font-bold text-slate-900">Hedef Net Tablosu</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium">Üniversite</th>
                <th className="px-4 py-2.5 font-medium">Bölüm</th>
                <th className="px-4 py-2.5 font-medium text-right">Hedef TYT</th>
                <th className="px-4 py-2.5 font-medium text-right">Hedef AYT</th>
                <th className="px-4 py-2.5 font-medium text-right">Mevcut TYT Ort.</th>
                <th className="px-4 py-2.5 font-medium text-right">Mevcut AYT Ort.</th>
                <th className="px-4 py-2.5 font-medium">Durum</th>
              </tr>
            </thead>
            <tbody>
              {data.targets.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Hedef tanımlı değil.</td></tr>
              ) : (
                data.targets.map((t) => {
                  const tytOk = Number(t.target_tyt_net || 0) > 0 && data.avg_tyt_net >= Number(t.target_tyt_net) * 0.85;
                  const aytOk = Number(t.target_ayt_net || 0) > 0 && data.avg_ayt_net >= Number(t.target_ayt_net) * 0.85;
                  return (
                    <tr key={t.id} className="border-b border-slate-100">
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{t.university}</td>
                      <td className="px-4 py-2.5 text-slate-600">{t.department || "-"}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-blue-700 font-semibold">{Number(t.target_tyt_net || 0).toFixed(0)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-amber-700 font-semibold">{Number(t.target_ayt_net || 0).toFixed(0)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-slate-600">{data.avg_tyt_net}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-slate-600">{data.avg_ayt_net}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${tytOk ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-rose-100 text-rose-700 border-rose-200"}`}>
                            TYT {tytOk ? "yaklaşıyor" : "uzak"}
                          </span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${aytOk ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-rose-100 text-rose-700 border-rose-200"}`}>
                            AYT {aytOk ? "yaklaşıyor" : "uzak"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

